import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award, Download, FileText, Target, TrendingUp, IndianRupee, MapPin, UserCheck, Users } from "lucide-react";
import { TARGET_STATUS_META } from "../../components/SupervisorTargetDialog";
import { toast } from "sonner";
import { PageHeader, Spinner, EmptyState } from "../../components/common";
import { CollectionsReconciliation } from "../../components/CollectionsReconciliation";
import { FetchButton, FetchForm, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { Badge } from "../../components/ui/badge";
import { StaffAvatar, StaffLabel } from "../../components/StaffAvatar";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Progress } from "../../components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { api, errMsg, fmtINR, downloadBlob } from "../../lib/api";
import { useLiveCollections } from "../../lib/liveCollections";

const RANK_CLS = {
  1: "bg-amber-100 text-amber-800 border-amber-300",
  2: "bg-slate-100 text-slate-700 border-slate-300",
  3: "bg-orange-100 text-orange-800 border-orange-300",
};

export default function PerformanceReport() {
  const { code } = useParams();
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState("");
  // Phase 133: the report runs only on Fetch, for the month applied at that moment
  const gate = useFetchGate({ month });
  const appliedMonth = gate.applied?.month;

  const load = useCallback(() => {
    if (!appliedMonth) return Promise.resolve();
    setLoading(true);
    return api.get(`/t/${code}/reports/monthly-performance`, { params: { month: appliedMonth } }).then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [code, appliedMonth]);
  const live = useLiveCollections(code);   // Phase 149
  useEffect(() => { if (gate.fetched) load().catch((e) => toast.error(errMsg(e))); }, [load, gate.fetched, gate.version, live]);

  const download = async (fmt) => {
    setDownloading(fmt);
    try {
      await downloadBlob(`/t/${code}/reports/monthly-performance?month=${appliedMonth}&fmt=${fmt}`,
        `${code}_performance_${appliedMonth}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) { toast.error(errMsg(e)); } finally { setDownloading(""); }
  };

  const header = (
    <PageHeader title="Monthly Performance Report"
      subtitle={data && gate.fetched ? `Supervisor teams ranked against monthly targets • day ${data.elapsed_days} of ${data.days_in_month}` : "Supervisor teams ranked against monthly targets — pick a month and click Fetch"}>
      <FetchForm onFetch={gate.fetch} className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs text-muted-foreground">Month</Label>
        <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
          className="w-44 cursor-pointer" data-testid="performance-month-input" />
        <FetchButton type="submit" loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="performance-fetch-button" />
        <Button type="button" variant="outline" onClick={() => download("pdf")} disabled={!!downloading || !gate.fetched}
          data-testid="performance-download-pdf">
          <FileText className="h-4 w-4 mr-2" /> {downloading === "pdf" ? "Preparing..." : "PDF"}
        </Button>
        <Button type="button" variant="outline" onClick={() => download("xlsx")} disabled={!!downloading || !gate.fetched}
          data-testid="performance-download-excel">
          <Download className="h-4 w-4 mr-2" /> {downloading === "xlsx" ? "Preparing..." : "Excel"}
        </Button>
      </FetchForm>
    </PageHeader>
  );

  if (!gate.fetched) {
    return (
      <div data-testid="performance-report-page">
        {header}
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="performance-fetch-prompt"
            subtitle="Pick the month above, then click Fetch to rank supervisor teams against their targets." />
        </CardContent></Card>
      </div>
    );
  }
  if (!data) return <div data-testid="performance-report-page">{header}<Spinner /></div>;

  return (
    <div data-testid="performance-report-page">
      {header}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Total target</p>
          <p className="text-lg font-semibold tabular-nums" data-testid="performance-total-target">{fmtINR(data.total_target)}</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5" /> Collected · all receipts</p>
          <p className="text-lg font-semibold tabular-nums" data-testid="performance-total-collected">{fmtINR(data.total_collected)}</p>
          <p className="text-[11px] text-muted-foreground tabular-nums">{fmtINR(data.team_collected_total ?? data.total_collected)} credited to collectors</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><TrendingUp className="h-3.5 w-3.5" /> Achievement</p>
          <p className="text-lg font-semibold tabular-nums">{data.total_achievement_pct != null ? `${data.total_achievement_pct}%` : "—"}</p>
          <p className="text-[11px] text-muted-foreground">collector targets vs credited</p>
        </CardContent></Card>
        <Card className="shadow-sm"><CardContent className="p-4">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Award className="h-3.5 w-3.5" /> Teams</p>
          <p className="text-lg font-semibold tabular-nums">{data.teams.length}</p>
        </CardContent></Card>
      </div>

      {data.others && (
        <div className="mb-4">
          <CollectionsReconciliation
            unified={{ amount: data.total_collected, receipts: data.total_receipts }}
            credited={{ amount: data.credited_total ?? data.team_collected_total, receipts: (data.team_receipts_total || 0) + (data.teams || []).reduce((n, t) => n + (t.personal_receipts || 0), 0) }}
            others={data.others} creditedLabel="Credited to collectors + supervisors' own" rangeLabel={appliedMonth}
            collectionsHref={`/${code}/admin/collections?date_from=${appliedMonth}-01&date_to=${appliedMonth}-31`}
            testId="performance-reconciliation" />
        </div>
      )}

      {data.teams.length === 0 ? (
        <EmptyState title="No supervisor teams yet" subtitle="Map tax collectors to supervisors in Users & Staff to build the report." />
      ) : (
        <div className="space-y-4" data-testid="performance-teams-list">
          {data.teams.map((t) => {
            const pct = t.team_target ? Math.min(100, Math.round((t.team_collected / t.team_target) * 100)) : 0;
            return (
              <Card key={t.supervisor_id || "unmapped"} className="shadow-sm" data-testid={`performance-team-${t.rank}`}>
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <Badge variant="outline" className={`text-sm font-bold px-2.5 py-1 ${RANK_CLS[t.rank] || ""}`}>#{t.rank}</Badge>
                      {t.supervisor_id && <StaffAvatar code={code} userId={t.supervisor_id} name={t.supervisor_name} size="md" testId={`performance-supervisor-avatar-${t.rank}`} />}
                      <div className="min-w-0">
                        <p className="font-medium flex items-center gap-2 flex-wrap">
                          <StaffLabel code={code} userId={t.supervisor_id} name={t.supervisor_name} />
                          {t.zone_names.map((z) => (
                            <Badge key={z} variant="outline" className="text-[10px] gap-0.5"><MapPin className="h-2.5 w-2.5" />{z}</Badge>
                          ))}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t.collectors.length} collector{t.collectors.length === 1 ? "" : "s"} • {t.team_receipts} receipts
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold tabular-nums">{fmtINR(t.team_collected)} <span className="text-xs text-muted-foreground font-normal">of {t.team_target ? fmtINR(t.team_target) : "no target"}</span></p>
                      <p className="text-xs text-muted-foreground">{t.achievement_pct != null ? `${t.achievement_pct}% achieved` : "No target set"}</p>
                    </div>
                  </div>
                  {t.team_target > 0 && <Progress value={pct} className="h-2" />}
                  {t.supervisor_id && (
                    <div className="grid sm:grid-cols-2 gap-2" data-testid={`performance-supervisor-targets-${t.rank}`}>
                      {[
                        { icon: Users, label: "Team goal (Admin)", target: t.team_goal, collected: t.team_collected, pct: t.goal_achievement_pct, status: t.goal_status, id: "goal" },
                        { icon: UserCheck, label: "Supervisor personal (own collections)", target: t.personal_target, collected: t.personal_collected, pct: t.personal_achievement_pct, status: t.personal_status, id: "personal" },
                      ].map((x) => {
                        const meta = TARGET_STATUS_META[x.status] || TARGET_STATUS_META.no_target;
                        const bar = x.target ? Math.min(100, Math.round((x.collected || 0) * 100 / x.target)) : 0;
                        return (
                          <div key={x.id} className="rounded-md border bg-muted/30 px-3 py-2" data-testid={`performance-${x.id}-${t.rank}`}>
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-muted-foreground flex items-center gap-1"><x.icon className="h-3 w-3" /> {x.label}</p>
                              <Badge variant="outline" className={`text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                            </div>
                            <p className="text-sm font-semibold tabular-nums">{fmtINR(x.collected)} <span className="text-xs font-normal text-muted-foreground">/ {x.target != null ? fmtINR(x.target) : "no target"}</span></p>
                            {x.target ? (
                              <div className="flex items-center gap-2 mt-1">
                                <Progress value={bar} className="h-1.5" />
                                <span className="text-[11px] tabular-nums text-muted-foreground w-10 text-right">{x.pct}%</span>
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Collector</TableHead>
                        <TableHead className="hidden md:table-cell">Wards</TableHead>
                        <TableHead className="text-right">Target</TableHead>
                        <TableHead className="text-right">Collected</TableHead>
                        <TableHead className="text-right">Achievement</TableHead>
                        <TableHead className="text-right hidden md:table-cell">Receipts</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {t.collectors.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className="font-medium">
                            <span className="flex items-center gap-2">
                              <StaffAvatar code={code} userId={c.id} name={c.name} size="xs" />
                              <span>
                                <StaffLabel code={code} userId={c.id} name={c.name} empCode={c.employee_id} />
                                {c.is_sub && <span className="block text-[10px] font-normal text-muted-foreground">Sub-user of <StaffLabel code={code} userId={c.parent_user_id} name={c.parent_user_name} /></span>}
                              </span>
                            </span>
                          </TableCell>
                          <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{c.ward_names.join(", ") || "—"}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.target != null ? fmtINR(c.target) : "—"}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{fmtINR(c.collected)}</TableCell>
                          <TableCell className="text-right tabular-nums">{c.achievement_pct != null ? `${c.achievement_pct}%` : "—"}</TableCell>
                          <TableCell className="text-right tabular-nums hidden md:table-cell">{c.receipts}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
